import React, { useMemo, useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import Datetime from "react-datetime";
import moment from "moment";
import { ThreeDots } from "react-loader-spinner";
import { useTranslation } from "react-i18next";
import AuthContext from "../../contexts/AuthProvider";
import { canDo } from "../../utils/routeAccess";
import useThemeColors from "../../hooks/useThemeColors";

// @material-ui/icons
import {
  Cancel,
  Check,
  Clear,
  Delete,
  Save,
  Refresh,
  ExpandMore,
  ExpandLess,
} from "@material-ui/icons";

// core components
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";
import CustomInput from "components/CustomInput/CustomInput";
import Button from "components/CustomButtons/Button";
import FilterDropdown from "components/FilterDropdown/FilterDropdown";
import Tooltip from "@material-ui/core/Tooltip";
import InputAdornment from "@material-ui/core/InputAdornment";

// Custom components
import CustomReactSelect from "../CustomDropdown/CustomReactSelect";

// styles
import { makeStyles } from "@material-ui/core/styles";
import {
  blendColors,
  grayColor,
  successColor,
  dangerColor,
  warningColor,
  whiteColor,
} from "../../assets/jss/material-dashboard-pro-react";

// Debounce utility function
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Custom hook to get all the context values
const useInitiativeContext = () => {
  const { t, i18n } = useTranslation();
  const { userDetails } = useContext(AuthContext);
  const { clientColor, clientTextColor } = useThemeColors();

  return { t, i18n, userDetails, clientColor, clientTextColor };
};

// Optimized Long Description Input Component
const LongDescriptionInput = React.forwardRef(
  ({ value, onChange, disabled }, ref) => {
    const [localValue, setLocalValue] = useState(value);
    const debouncedValue = useDebounce(localValue, 300); // 300ms debounce

    // Update local value when prop value changes
    useEffect(() => {
      setLocalValue(value);
    }, [value]);

    // Update parent when debounced value changes
    useEffect(() => {
      if (debouncedValue !== value) {
        onChange(debouncedValue);
      }
    }, [debouncedValue, onChange, value]);

    const handleChange = (e) => {
      setLocalValue(e.target.value);
    };

    const handleBlur = (e) => {
      const trimmedValue = e.target.value.trim();
      setLocalValue(trimmedValue);
      onChange(trimmedValue);
    };

    return (
      <CustomInput
        id="detailed_description"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        formControlProps={{
          fullWidth: true,
          style: { paddingTop: "2px" },
        }}
        inputProps={{
          autoComplete: "off",
          disabled: disabled,
        }}
        client
        multiline
        ref={ref}
        minRows={3}
        maxRows={6}
      />
    );
  },
);

LongDescriptionInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

LongDescriptionInput.displayName = "LongDescriptionInput";

// DatePickerBenefitDates component
const DatePickerBenefitDates = ({
  value,
  onChange,
  initiativesBusy,
  selectedInitiative,
  initiatives,
  isValidDate,
}) => {
  const { i18n } = useInitiativeContext();

  const renderInput = (props) => {
    return (
      <div>
        <CustomInput
          formControlProps={{
            fullWidth: true,
            style: { paddingTop: "0", marginTop: "-6px" },
          }}
          inputProps={{
            type: "text",
            autoComplete: "off",
            disabled:
              initiativesBusy ||
              (selectedInitiative !== "new" &&
                initiatives[selectedInitiative] &&
                initiatives[selectedInitiative].status !== "active"),
            // Removed endAdornment with status icons
          }}
          client
          timeFormat={false}
          {...props}
        />
      </div>
    );
  };

  return (
    <Datetime
      value={value}
      timeFormat={false}
      closeOnSelect={true}
      dateFormat="YYYY-MM"
      locale={i18n.language}
      renderInput={renderInput}
      onChange={onChange}
      isValidDate={isValidDate}
    />
  );
};

DatePickerBenefitDates.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func,
  status: PropTypes.bool,
  showStatusIcons: PropTypes.bool,
  initiativesBusy: PropTypes.bool,
  selectedInitiative: PropTypes.any,
  initiatives: PropTypes.object,
  isValidDate: PropTypes.func,
};

const InitiativeDetailsCard = ({
  // Card state and data
  selectedInitiative,
  initiativeForm,
  setInitiativeFormValue,
  // Status flags
  initiativesBusy,
  screenBusy,
  // Data arrays
  pnlStructure,
  tags,
  initiatives,
  // Functions
  checkAndSaveInitiative,
  updateInitData,
  handlers,
  // Filter related
  metaLoaded,
  filtersListTranslated,
  filtersActiveList,
  filtersSequence,
  filterMenuClickHandler,
  currentScopeFilters,
  hasNoScopeFilters,
  selectedTagsFilters,
  // Refs
  shortDescriptionInput,
  // Additional props for longDescription
  initLongDescriptionRef,
}) => {
  // Use hooks directly instead of receiving as props
  const { t, userDetails, clientColor, clientTextColor } =
    useInitiativeContext();

  // Capacidade de deletar iniciativa (atomo no sistema novo; roles de
  // manage_initiatives no legado). O guard extra de "deletar concluida =
  // so DEV/IMG" continua valendo por cima.
  const canDeleteInitiative = canDo(
    userDetails,
    "governance.initiatives.delete",
    ["DEV", "IMG", "CAD", "CST", "CFA", "CVW"],
  );

  // Local state for description visibility
  const [isDetailedDescriptionVisible, setIsDetailedDescriptionVisible] =
    useState(false);

  // Local styles object
  const styles = {
    card: {
      marginTop: "10px",
      "&.newInitiative": {
        border: "1px " + clientColor + " solid",
      },
    },
    cardCategory: {
      color: "#ffffff",
      fontSize: "14px",
      paddingTop: "5px",
      marginBottom: "0",
      marginTop: "0",
      margin: "0",
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      "&.italic": {
        fontStyle: "italic !important",
        paddingRight: "4px",
      },
    },
    initiativesSpinner: {
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "& > svg": {
        width: "40px !important",
      },
    },
    invisible: {
      opacity: "0",
      pointerEvents: "none",
    },
    cardButton: {
      padding: "0 !important",
      margin: "5px 0 0 5px !important",
      height: "22px !important",
      width: "30px !important",
      minWidth: "0 !important",
      position: "relative",
      transition: "all 0.2s",
    },
    cardButtonIcon: {
      margin: "0 !important",
      position: "absolute !important",
      top: "50% !important",
      left: "50% !important",
      transform: "translate(-50%, -50%)",
      width: "18px !important",
      height: "18px !important",
    },
    cardButtonDisabled: {
      "& svg, & .MuiSvgIcon-root": {
        opacity: "0.65 !important",
        color: "rgba(255, 255, 255, 0.65) !important",
      },
    },
    attention: {
      backgroundColor: warningColor[0] + " !important",
      animation: "$attention 3s ease infinite",
    },
    "@keyframes attention": {
      "10%": {
        transform: "scale(1.1)",
      },
      "13.33%,20%": {
        transform: "rotate(-10deg) scale(1.1)",
      },
      "16.67%": {
        transform: "rotate(10deg) scale(1.1)",
      },
      "23.33%": {
        transform: "rotate(0deg) scale(1.1)",
      },
      "33%": {
        transform: "scale(1)",
      },
    },
    inputDescription: {
      fontSize: "11px",
      marginBottom: "-2px",
    },
    inputAdornment: {
      marginRight: "8px",
      position: "relative",
    },
    inputAdornmentIconSuccess: {
      color: successColor[0] + "!important",
    },
    inputAdornmentIconError: {
      color: dangerColor[0] + "!important",
    },
    topCardShortSpacing: {
      margin: "0px",
    },
    topCardBody: {
      paddingTop: "5px !important",
      paddingBottom: "5px !important",
      paddingLeft: "0px !important",
    },
    refreshIcon: {
      color: grayColor[1],
      marginBottom: "5px",
      marginLeft: "-20px",
      fontSize: "18px",
      cursor: "pointer",
      transition: "transform 150ms",
    },
    // Stage button styles (based on view button pattern)
    stageButtonContainer: {
      display: "flex",
      justifyContent: "center", // Center horizontally
      alignItems: "center", // Center vertically
      width: "100%",
      minWidth: "0", // Prevents flex items from overflowing
      height: "100%", // Take full height of container
    },
    stageButton: {
      margin: "0 !important",
      position: "relative",
      boxShadow: "0 0 2px rgba(0, 0, 0, 0.3) !important",
      padding: "3px 8px !important",
      height: "30px",
      fontSize: "12px",
      flex: 1, // Take equal space to fill container
      minWidth: "fit-content", // Minimum width based on content
      color: "#aaaaaa !important",
      whiteSpace: "nowrap",
    },
    stageButtonFirst: {
      borderTopRightRadius: "0 !important",
      borderBottomRightRadius: "0 !important",
    },
    stageButtonMiddle: {
      borderRadius: "0 !important",
    },
    stageButtonLast: {
      borderTopLeftRadius: "0 !important",
      borderBottomLeftRadius: "0 !important",
    },
    stageButtonSelected: {
      pointerEvents: "none",
      // backgroundColor: "#555555 !important",
      backgroundColor: clientColor + " !important",
      color: "#ffffff !important",
    },

    // Custom styles for P&L select that match Pnl component styling
    pnlSelectContainer: {
      width: "100%",
      marginTop: "-8px",
    },
    // Styles for description toggle button
    descriptionToggleButton: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "20px",
      height: "20px",
      marginLeft: "8px",
      padding: "0",
      minWidth: "20px",
      borderRadius: "50%",
      backgroundColor: "transparent",
      color: grayColor[7],
      cursor: "pointer",
      border: "none",
      outline: "none",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: grayColor[13],
        color: grayColor[6],
      },
      "&:focus": {
        outline: "none",
      },
      "& svg": {
        fontSize: "16px",
        transition: "transform 0.2s ease",
      },
    },
    descriptionLabelContainer: {
      display: "flex",
      alignItems: "center",
      marginBottom: "2px",
    },
  };

  const useStyles = makeStyles(() => styles);
  const classesLocal = useStyles();

  // Function to validate date relationships for the overall form validation
  const validateDateRelationships = () => {
    const projectStart = initiativeForm.projectStart;
    const benefitStart = initiativeForm.benefitStart;
    const benefitEnd = initiativeForm.benefitEnd;

    // If any date is not set, consider it valid (user can still fill it)
    if (!projectStart || !benefitStart || !benefitEnd) {
      return true;
    }

    // Check chronological order: project start → capture start → capture full potential
    const isProjectStartValid =
      moment.isMoment(projectStart) &&
      projectStart.isSameOrAfter(moment().startOf("month")); // Current month or future

    const isCaptureStartValid =
      moment.isMoment(benefitStart) &&
      moment.isMoment(projectStart) &&
      benefitStart.isSameOrAfter(projectStart);

    const isCaptureFullPotentialValid =
      moment.isMoment(benefitEnd) &&
      moment.isMoment(benefitStart) &&
      benefitEnd.isSameOrAfter(benefitStart);

    return (
      isProjectStartValid && isCaptureStartValid && isCaptureFullPotentialValid
    );
  };

  // Simple validation function for individual date inputs (all dates enabled)
  const isValidDate = (currentDate) => {
    // Only basic validation: date should be after 1 year ago
    return currentDate.isAfter(moment().subtract(1, "year"));
  };

  // Compute tagNames and activeTagIndexes internally
  const tagNames = tags.map((t) => t.name);
  const activeTagIndexes = tags
    .map((tag, index) => (initiativeForm.tags.includes(tag.id) ? index : -1))
    .filter((index) => index !== -1);

  // Integrated handleInputChange function
  const handleInputChange = (event, identifier) => {
    if (event.target) {
      const value = event.target.value;
      const fieldMap = {
        short_description: "shortDescription",
        leader: "leader",
        benefit: "benefit",
      };

      if (fieldMap[event.target.id]) {
        setInitiativeFormValue(fieldMap[event.target.id], value);
      }
      updateInputStatus(event.target.id, value);
    } else {
      if (
        identifier === "project_start" ||
        identifier === "benefits_start" ||
        identifier === "benefits_end"
      ) {
        const fieldMap = {
          project_start: "projectStart",
          benefits_start: "benefitStart",
          benefits_end: "benefitEnd",
        };

        // Handle different types of date input values
        let dateValue = null;
        if (moment.isMoment(event)) {
          // Valid moment object selected
          dateValue = event;
        } else if (typeof event === "string" && event.trim() !== "") {
          // String input - try to parse it as a date
          const parsedDate = moment(event, "YYYY-MM", true);
          if (parsedDate.isValid()) {
            dateValue = parsedDate;
          }
        }
        // If event is null, undefined, or invalid, dateValue remains null
        setInitiativeFormValue(fieldMap[identifier], dateValue);
      }
      updateInputStatus(identifier, event);
    }
  };

  // Integrated handleInputBlur function
  const handleInputBlur = (event) => {
    if (event.target.id === "short_description") {
      const strippedValue = utils.stripText(initiativeForm.shortDescription);
      setInitiativeFormValue("shortDescription", strippedValue);
      setInitiativeFormValue(
        "shortDescriptionPrinted",
        strippedValue !== "" ? strippedValue : null,
      );
    } else if (event.target.id === "leader") {
      setInitiativeFormValue("leader", utils.stripText(initiativeForm.leader));
    } else if (event.target.id === "benefit") {
      try {
        let val = parseFloat(initiativeForm.benefit);
        if (isNaN(val)) {
          setInitiativeFormValue("benefit", "0.00");
        } else {
          val = Math.min(Math.max(val, 0), 20);
          setInitiativeFormValue("benefit", val.toFixed(2));
        }
      } catch (e) {
        setInitiativeFormValue("benefit", "0.00");
      }
    }
  };

  // Integrated inputsStatusOk function
  const inputsStatusOk = () => {
    // For opportunity stage, only name is required (leader and benefit are optional, dates are optional)
    if (initiativeForm.stage === "opportunity") {
      return [initiativeForm.shortDescriptionStatus].every(Boolean);
    }

    // For other stages, all mandatory fields are required
    return [
      initiativeForm.shortDescriptionStatus,
      initiativeForm.leaderStatus,
      initiativeForm.benefitStatus,
      initiativeForm.projectStartStatus,
      initiativeForm.benefitStartStatus,
      initiativeForm.benefitEndStatus,
    ].every(Boolean);
  };

  // Helper functions for card completion status
  const isDescriptionComplete = () => {
    // For opportunity stage, leader is optional
    if (initiativeForm.stage === "opportunity") {
      return initiativeForm.shortDescriptionStatus;
    }

    return initiativeForm.shortDescriptionStatus && initiativeForm.leaderStatus;
  };

  const isBenefitComplete = () => {
    // For opportunity stage, benefit is optional
    if (initiativeForm.stage === "opportunity") {
      return true;
    }

    return initiativeForm.benefitStatus;
  };

  const isDatesComplete = () => {
    // For opportunity stage, dates are optional
    if (initiativeForm.stage === "opportunity") {
      return true;
    }

    return (
      initiativeForm.projectStartStatus &&
      initiativeForm.benefitStartStatus &&
      initiativeForm.benefitEndStatus &&
      validateDateRelationships()
    );
  };

  // Transform pnlStructure to CustomReactSelect options format
  const pnlSelectOptions = useMemo(() => {
    if (!pnlStructure || !Array.isArray(pnlStructure)) return [];

    return pnlStructure.map((line) => ({
      value: line.value,
      label: line.label, // Use label property from pnlStructure
      level: line.level, // Include level for styling
    }));
  }, [pnlStructure]);

  // Handle P&L line selection
  const handlePnlLineChange = (selectedOption) => {
    setInitiativeFormValue(
      "pnlImpactLine",
      selectedOption ? selectedOption.value : null,
    );
  };

  // Get current P&L line value for select
  const currentPnlLineValue = useMemo(() => {
    if (!initiativeForm.pnlImpactLine || !pnlSelectOptions.length) return null;

    return (
      pnlSelectOptions.find(
        (option) => option.value === initiativeForm.pnlImpactLine,
      ) || null
    );
  }, [initiativeForm.pnlImpactLine, pnlSelectOptions]);

  // Custom styles for P&L select that match Pnl component styling
  const pnlSelectCustomStyles = useMemo(
    () => ({
      control: (provided, state) => ({
        ...provided,
        borderRadius: "0",
        borderWidth: "0 0 1px 0",
        borderColor: state.isFocused ? clientColor : grayColor[4],
        marginTop: "-8px",
        boxShadow: "none",
        minHeight: "35px",
        "&:hover": {
          borderColor: clientColor,
        },
      }),
      option: (provided, state) => {
        // Get the option data to determine styling based on level
        const optionData = state.data;
        const level = optionData?.level || 1;

        // Base styles
        let backgroundColor = "white";
        let color = grayColor[0];
        let fontWeight = "normal";
        let paddingLeft = "12px";

        color = grayColor[1]; // Use same font color as other levels
        // Apply styling based on level (similar to Pnl component)
        if (level === 1) {
          // Level 1 (Total lines) - bold, uppercase, dark background
          backgroundColor = state.isFocused
            ? grayColor[9] // Darker shade of gray for hover effect (applies to both selected and non-selected)
            : state.isSelected
              ? grayColor[8] // Selected option - original dark background
              : grayColor[8];
          fontWeight = "bold";
        } else if (level === 2) {
          // Level 2 (Subtotal lines) - light gray background
          backgroundColor = state.isFocused
            ? grayColor[9] // Darker shade of gray for hover effect (applies to both selected and non-selected)
            : state.isSelected
              ? grayColor[13] // Selected option - original light background
              : grayColor[13];
          fontWeight = "normal";
        } else if (level >= 3) {
          // Level 3+ (Detail lines) - white background, lighter text, progressive indent
          backgroundColor = state.isFocused
            ? grayColor[9] // Darker shade of gray for hover effect (applies to both selected and non-selected)
            : state.isSelected
              ? "white" // Selected option - original white background
              : "white";
          fontWeight = "normal";
          paddingLeft = `${12 + (level - 2) * 14}px`; // Progressive indent for level 3+
        }

        return {
          ...provided,
          padding: `6px ${paddingLeft} 6px 12px`, // Decreased vertical padding by 2px
          color: color,
          backgroundColor: backgroundColor,
          fontSize: "12px",
          lineHeight: "1.5",
          fontWeight: fontWeight,
          textTransform: level === 1 ? "uppercase" : "none",
          "&:active": {
            backgroundColor: grayColor[8],
          },
        };
      },
      valueContainer: (provided) => ({
        ...provided,
        padding: "0",
      }),
      menu: (provided) => ({
        ...provided,
        marginTop: "2px",
        zIndex: "9999",
        boxShadow: "0 2px 5px 0 rgba(0, 0, 0, 0.26)",
        minWidth: "200px", // Increased width of options area
        maxHeight: "300px", // Limit height to ensure scrolling
        overflow: "auto", // Ensure scrolling is enabled
      }),
      menuList: (provided) => ({
        ...provided,
        maxHeight: "200px", // Ensure consistent height
        overflow: "auto", // Enable scrolling
        scrollbarWidth: "thin", // For Firefox
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-track": {
          background: grayColor[13],
        },
        "&::-webkit-scrollbar-thumb": {
          background: grayColor[8],
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: grayColor[9],
        },
      }),
      singleValue: (provided) => {
        // Style the selected value - use simple styling without level-based formatting
        return {
          ...provided,
          marginTop: "4px",
          color: grayColor[14], // Same color as other regular inputs
          fontSize: "14px",
          fontWeight: "300", // Font weight 300
          textTransform: "none",
        };
      },
      placeholder: (provided) => ({
        ...provided,
        color: grayColor[7],
        fontSize: "13px",
        fontStyle: "italic",
      }),
      // Remove the clear indicator (X button) when an option is selected
      clearIndicator: () => ({
        display: "none",
      }),
      // Remove the dropdown indicator when an option is selected
      indicatorsContainer: (provided, state) => ({
        ...provided,
        // Hide the clear indicator (X button)
        "& > div:last-child": {
          display: state.hasValue ? "none" : "flex",
        },
      }),
      // Remove any separators or dividers
      separator: () => ({
        display: "none",
      }),
      indicatorSeparator: () => ({
        display: "none",
      }),
    }),
    [],
  );

  // Integrated isSavePending function
  const isSavePending = () => {
    const init = initiatives[selectedInitiative];
    if (!init) return false;

    // Helper function to format dates consistently
    const formatDate = (date) => (date ? date.format("YYYY-MM") : null);

    // Helper function to format numbers consistently
    const formatNumber = (value) => parseFloat(value).toFixed(2);

    // Compare all fields efficiently
    const changes = [
      {
        original: init.short_description,
        current: initiativeForm.shortDescription,
      },
      { original: init.leader, current: initiativeForm.leader },
      {
        original: formatNumber(100 * init.margin_benefit),
        current: formatNumber(initiativeForm.benefit),
      },
      {
        original: init.long_description,
        current: initiativeForm.longDescription,
      },
      {
        original: formatNumber(100 * init.margin_benefit_ratio),
        current: formatNumber(initiativeForm.scopeMarginRatio),
      },
      { original: init.stage, current: initiativeForm.stage },
      {
        original: init.project_start || null,
        current: formatDate(initiativeForm.projectStart),
      },
      {
        original: init.margin_benefit_start || null,
        current: formatDate(initiativeForm.benefitStart),
      },
      {
        original: init.margin_benefit_end || null,
        current: formatDate(initiativeForm.benefitEnd),
      },
      { original: init.pnl_impact_line, current: initiativeForm.pnlImpactLine },
      {
        original: JSON.stringify((init.tags || []).map((tag) => tag.id).sort()),
        current: JSON.stringify([...initiativeForm.tags].sort()),
      },
    ];

    return changes.some(({ original, current }) => original !== current);
  };

  // Helper function for input validation (needed by handleInputChange)
  const updateInputStatus = (fieldId, inputValue) => {
    const fieldMap = {
      short_description: "shortDescriptionStatus",
      leader: "leaderStatus",
      benefit: "benefitStatus",
      project_start: "projectStartStatus",
      benefits_start: "benefitStartStatus",
      benefits_end: "benefitEndStatus",
    };

    let isValid = false;

    // Validate based on field type
    switch (fieldId) {
      case "short_description": {
        const strippedDesc = utils.stripText(inputValue);
        isValid = strippedDesc.length >= 3 && strippedDesc.length <= 60;
        break;
      }
      case "leader": {
        const strippedLeader = utils.stripText(inputValue);
        // For opportunity stage, leader is optional (empty or valid)
        if (initiativeForm.stage === "opportunity") {
          isValid =
            strippedLeader.length === 0 ||
            (strippedLeader.length >= 2 && strippedLeader.length <= 20);
        } else {
          // For other stages, leader is mandatory
          isValid = strippedLeader.length >= 2 && strippedLeader.length <= 20;
        }
        break;
      }
      case "benefit": {
        const num = parseFloat(inputValue);
        // For opportunity stage, benefit is optional (can be 0)
        if (initiativeForm.stage === "opportunity") {
          isValid = !isNaN(num) && num >= 0 && num <= 20;
        } else {
          // For other stages, benefit must be greater than 0
          isValid = !isNaN(num) && num > 0 && num <= 20;
        }
        break;
      }
      case "project_start":
      case "benefits_start":
      case "benefits_end":
        if (!inputValue) {
          isValid = false;
        } else {
          // Alternative validation approach to avoid scope issues
          try {
            // Check if it's a moment object by testing for moment-specific methods
            if (
              inputValue &&
              typeof inputValue.format === "function" &&
              typeof inputValue.isAfter === "function"
            ) {
              const oneYearAgo = moment().subtract(1, "year");
              isValid = inputValue.isAfter(oneYearAgo);
            } else {
              isValid = false;
            }
          } catch (error) {
            isValid = false;
          }
        }
        break;
      default:
        isValid = false;
    }

    if (fieldMap[fieldId]) {
      setInitiativeFormValue(fieldMap[fieldId], isValid);
    }
  };

  // Helper utils object (needed by handleInputBlur)
  const utils = {
    stripText: (text) => text.replace(/^\s+|\s+$/g, ""),
  };

  return (
    <>
      {/* First Card - Header with initiative name and buttons */}
      <Card
        className={cx({
          [classesLocal.card]: true,
        })}
        style={{
          marginBottom: "10px",
          height: "32px",
          // backgroundColor: blendColors(clientColor, grayColor[6], 0.5),
          backgroundImage: `linear-gradient(to right, ${blendColors(clientColor, grayColor[6], 0.5)} , ${clientColor})`,
        }}
      >
        <CardHeader
          style={{ padding: "0 6px 10px 12px", position: "relative" }}
        >
          <GridContainer>
            <GridItem>
              <p
                className={classesLocal.cardCategory}
                style={{
                  fontWeight: "400",
                  textTransform: "uppercase",
                  paddingRight: "10px",
                  color: clientTextColor,
                }}
              >
                {t("initiatives.initiative_details")}
              </p>
            </GridItem>
            <GridItem xs={true}></GridItem>
            <GridItem
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <ThreeDots
                color={whiteColor}
                height="18px"
                ariaLabel="loading"
                wrapperStyle={{ marginTop: "8px" }}
                wrapperClass={cx({
                  [classesLocal.initiativesSpinner]: true,
                  [classesLocal.invisible]: !initiativesBusy,
                })}
              />
            </GridItem>
            <GridItem>
              {selectedInitiative !== "new" && (
                <Tooltip
                  placement={"top"}
                  title={t("initiatives.delete")}
                  PopperProps={{
                    style: { marginBottom: "-8px" },
                  }}
                >
                  <span>
                    <Button
                      justIcon
                      color="danger"
                      className={cx({
                        [classesLocal.cardButton]: true,
                        [classesLocal.cardButtonDisabled]:
                          screenBusy ||
                          initiativesBusy ||
                          !canDeleteInitiative ||
                          (selectedInitiative !== "new" &&
                            selectedInitiative !== null &&
                            initiatives[selectedInitiative] &&
                            initiatives[selectedInitiative].stage ===
                              "concluded" &&
                            !["DEV", "IMG"].includes(userDetails?.user_role)),
                      })}
                      onClick={handlers.deleteInitiative}
                      disabled={
                        screenBusy ||
                        initiativesBusy ||
                        !canDeleteInitiative ||
                        (selectedInitiative !== "new" &&
                          selectedInitiative !== null &&
                          initiatives[selectedInitiative] &&
                          initiatives[selectedInitiative].stage ===
                            "concluded" &&
                          !["DEV", "IMG"].includes(userDetails?.user_role))
                      }
                    >
                      <div style={{ width: "100%" }}>
                        <Delete className={classesLocal.cardButtonIcon} />
                      </div>
                    </Button>
                  </span>
                </Tooltip>
              )}

              {selectedInitiative === "new" && (
                <Tooltip
                  placement={"top"}
                  title={t("initiatives.cancel_addition")}
                  PopperProps={{
                    style: { marginBottom: "-6px" },
                  }}
                >
                  <Button
                    justIcon
                    color="warning"
                    className={cx({
                      [classesLocal.cardButton]: true,
                      [classesLocal.cardButtonDisabled]:
                        screenBusy || initiativesBusy,
                    })}
                    disabled={screenBusy || initiativesBusy}
                    onClick={handlers.cancelAddition}
                  >
                    <div style={{ width: "100%" }}>
                      <Cancel className={classesLocal.cardButtonIcon} />
                    </div>
                  </Button>
                </Tooltip>
              )}
              <Tooltip
                placement={"top"}
                title={t("initiatives.save")}
                PopperProps={{
                  style: { marginBottom: "-8px" },
                }}
              >
                <span>
                  <Button
                    justIcon
                    color="client"
                    className={cx({
                      [classesLocal.cardButton]: true,
                      [classesLocal.attention]:
                        inputsStatusOk() && isSavePending() && !initiativesBusy,
                      [classesLocal.cardButtonDisabled]:
                        screenBusy || initiativesBusy || !inputsStatusOk(),
                    })}
                    disabled={
                      screenBusy || initiativesBusy || !inputsStatusOk()
                    }
                    onClick={() => checkAndSaveInitiative()}
                  >
                    <div style={{ width: "100%" }}>
                      <Save className={classesLocal.cardButtonIcon} />
                    </div>
                  </Button>
                </span>
              </Tooltip>
            </GridItem>
          </GridContainer>
        </CardHeader>
      </Card>

      {/* Second Card - Description Title and Inputs Side by Side */}
      <div style={{ display: "flex", marginBottom: "10px" }}>
        {/* Title Card - 15% width */}
        <div style={{ width: "15%", marginRight: "10px" }}>
          <Card
            className={cx({
              [classesLocal.card]: true,
            })}
            style={{ height: "100%", margin: "0" }}
          >
            <div
              style={{
                color: grayColor[3],
                backgroundColor: grayColor[8] + "44",
                textAlign: "center",
                padding: "15px 10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "column",
                }}
              >
                {t("initiatives.description")}
                {isDescriptionComplete() ? (
                  <Check style={{ color: successColor[0], fontSize: "20px" }} />
                ) : (
                  <Clear style={{ color: dangerColor[0], fontSize: "20px" }} />
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* Inputs Card - 85% width */}
        <div style={{ width: "85%" }}>
          <Card
            className={cx({
              [classesLocal.card]: true,
            })}
            style={{ height: "100%", margin: "0" }}
          >
            <CardBody style={{ padding: "10px 10px 0 10px" }}>
              <GridContainer>
                {/* Row 1: Description, Leader */}
                <GridItem xs={12} md={8}>
                  <label className={classesLocal.inputDescription}>
                    {t("initiatives.initiative_name")}
                  </label>
                  <CustomInput
                    id="short_description"
                    value={initiativeForm.shortDescription}
                    onChange={(e) => handleInputChange(e)}
                    onBlur={(e) => handleInputBlur(e)}
                    formControlProps={{
                      fullWidth: true,
                      style: { paddingTop: "0", marginTop: "-6px" },
                    }}
                    inputProps={{
                      type: "text",
                      maxLength: 60,
                      autoComplete: "off",
                      disabled:
                        initiativesBusy ||
                        (selectedInitiative !== "new" &&
                          initiatives[selectedInitiative] &&
                          initiatives[selectedInitiative].status !== "active"),
                      // Removed endAdornment with status icons
                    }}
                    client
                    ref={shortDescriptionInput}
                  />
                </GridItem>
                <GridItem xs={12} md={4}>
                  <label className={classesLocal.inputDescription}>
                    {t("initiatives.leader")}
                  </label>
                  <CustomInput
                    id="leader"
                    value={initiativeForm.leader}
                    onChange={(e) => handleInputChange(e)}
                    onBlur={(e) => handleInputBlur(e)}
                    formControlProps={{
                      fullWidth: true,
                      style: { paddingTop: "0", marginTop: "-6px" },
                    }}
                    inputProps={{
                      type: "text",
                      maxLength: 20,
                      autoComplete: "off",
                      disabled:
                        initiativesBusy ||
                        (selectedInitiative !== "new" &&
                          initiatives[selectedInitiative] &&
                          initiatives[selectedInitiative].status !== "active"),
                      // Removed endAdornment with status icons
                    }}
                    client
                  />
                </GridItem>
                {/* Row 2: Long Description */}
                <GridItem xs={12} style={{ marginTop: "-5px" }}>
                  <div className={classesLocal.descriptionLabelContainer}>
                    <label className={classesLocal.inputDescription}>
                      {t("initiatives.details_modal.detailed_description")}
                    </label>
                    <Tooltip
                      placement={"top"}
                      title={
                        isDetailedDescriptionVisible
                          ? `${t("common.hide")} ${t("initiatives.details_modal.detailed_description").toLowerCase()}`
                          : `${t("common.show")} ${t("initiatives.details_modal.detailed_description").toLowerCase()}`
                      }
                      PopperProps={{
                        style: { marginBottom: "-8px" },
                      }}
                    >
                      <button
                        type="button"
                        className={classesLocal.descriptionToggleButton}
                        onClick={() =>
                          setIsDetailedDescriptionVisible(
                            !isDetailedDescriptionVisible,
                          )
                        }
                      >
                        {isDetailedDescriptionVisible ? (
                          <ExpandLess />
                        ) : (
                          <ExpandMore />
                        )}
                      </button>
                    </Tooltip>
                  </div>
                  {isDetailedDescriptionVisible && (
                    <LongDescriptionInput
                      key={selectedInitiative}
                      value={initiativeForm.longDescription}
                      onChange={(value) =>
                        setInitiativeFormValue("longDescription", value)
                      }
                      disabled={
                        initiativesBusy ||
                        (selectedInitiative !== "new" &&
                          initiatives[selectedInitiative] &&
                          initiatives[selectedInitiative].status !== "active")
                      }
                      ref={initLongDescriptionRef}
                    />
                  )}
                </GridItem>
              </GridContainer>
            </CardBody>
          </Card>
        </div>
      </div>

      {/* Third Card - Scope Title and Content Side by Side */}
      <div style={{ display: "flex", marginBottom: "10px" }}>
        {/* Title Card - 15% width */}
        <div style={{ width: "15%", marginRight: "10px" }}>
          <Card
            className={cx({
              [classesLocal.card]: true,
            })}
            style={{ height: "100%", margin: "0" }}
          >
            <div
              style={{
                color: grayColor[3],
                backgroundColor: grayColor[8] + "44",
                textAlign: "center",
                padding: "15px 10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              {t("initiatives.scope")}
            </div>
          </Card>
        </div>

        {/* Content Card - 85% width */}
        <div style={{ width: "85%" }}>
          <Card
            className={cx({
              [classesLocal.card]: true,
            })}
            style={{ height: "100%", margin: "0" }}
          >
            <CardBody style={{ padding: "8px 15px 0 5px" }}>
              <GridContainer style={{ margin: "0" }}>
                <GridItem xs={12}>
                  <label className={classesLocal.inputDescription}>
                    {t("initiatives.scope")}
                  </label>
                  <GridContainer
                    alignItems={"center"}
                    style={{
                      marginBottom: "5px",
                      minHeight: "30px",
                    }}
                  >
                    <GridItem>
                      {metaLoaded && (
                        <FilterDropdown
                          dropPlacement={"bottom-start"}
                          hoverColor={"client"}
                          dropdownHeader={t("results.add_change_filter")}
                          dropdownList={filtersListTranslated()}
                          dropdownActiveItems={filtersActiveList()}
                          dropdownFilterSequence={filtersSequence()}
                          tooltip={t("results.add_change_filter")}
                          onClick={filterMenuClickHandler()}
                        />
                      )}
                    </GridItem>
                    <GridItem xs={true}>
                      {currentScopeFilters()}
                      {hasNoScopeFilters() && (
                        <span
                          style={{
                            color: "#999",
                            fontSize: "12px",
                            fontStyle: "italic",
                            marginLeft: "8px",
                          }}
                        >
                          {t("initiatives.no_filters_general_scope")}
                        </span>
                      )}
                      {!metaLoaded && (
                        <ThreeDots
                          color={clientColor}
                          ariaLabel="loading"
                          height={"22px"}
                          wrapperStyle={{}}
                          wrapperClass=""
                        />
                      )}
                    </GridItem>
                  </GridContainer>
                </GridItem>
              </GridContainer>
            </CardBody>
          </Card>
        </div>
      </div>

      {/* Fourth Card - Benefit Title and Content Side by Side */}
      <div style={{ display: "flex", marginBottom: "10px" }}>
        {/* Title Card - 15% width */}
        <div style={{ width: "15%", marginRight: "10px" }}>
          <Card
            className={cx({
              [classesLocal.card]: true,
            })}
            style={{ height: "100%", margin: "0" }}
          >
            <div
              style={{
                color: grayColor[3],
                backgroundColor: grayColor[8] + "44",
                textAlign: "center",
                padding: "15px 10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "column",
                }}
              >
                {t("initiatives.benefit")}
                {isBenefitComplete() ? (
                  <Check style={{ color: successColor[0], fontSize: "20px" }} />
                ) : (
                  <Clear style={{ color: dangerColor[0], fontSize: "20px" }} />
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* Content Card - 85% width */}
        <div style={{ width: "85%" }}>
          <Card
            className={cx({
              [classesLocal.card]: true,
            })}
            style={{ height: "100%", margin: "0" }}
          >
            <CardBody style={{ padding: "10px 15px 0 5px" }}>
              <GridContainer style={{ margin: "0" }}>
                {/* Row 2: P&L Line, Benefit, Scope Margin, Benefit Global */}
                <GridItem xs={12} md={4}>
                  <label
                    className={classesLocal.inputDescription}
                    style={{ position: "relative", zIndex: "1" }}
                  >
                    {t("initiatives.pnl_impact_line")}
                  </label>
                  <div className={classesLocal.pnlSelectContainer}>
                    <CustomReactSelect
                      options={pnlSelectOptions}
                      value={currentPnlLineValue}
                      onChange={handlePnlLineChange}
                      isClearable={true}
                      customStyles={pnlSelectCustomStyles}
                      placeholder={t("initiatives.pnl_line_not_set")}
                      isDisabled={
                        initiativesBusy ||
                        (selectedInitiative !== "new" &&
                          initiatives[selectedInitiative]?.status !== "active")
                      }
                    />
                  </div>
                </GridItem>
                {/* Benefit Input */}
                <GridItem xs={12} md={2}>
                  <label className={classesLocal.inputDescription}>
                    {t("initiatives.benefit")}
                  </label>
                  <CustomInput
                    id="benefit"
                    value={initiativeForm.benefit}
                    onChange={(e) => handleInputChange(e)}
                    onBlur={(e) => handleInputBlur(e)}
                    formControlProps={{
                      fullWidth: true,
                      style: { paddingTop: "0", marginTop: "-6px" },
                    }}
                    inputProps={{
                      type: "number",
                      autoComplete: "off",
                      step: "0.05",
                      disabled:
                        initiativesBusy ||
                        (selectedInitiative !== "new" &&
                          initiatives[selectedInitiative] &&
                          initiatives[selectedInitiative].status !== "active"),
                      // Removed endAdornment with status icons
                    }}
                    client
                  />
                </GridItem>

                {/* Scope Margin Ratio Input with 'x' integrated */}
                <GridItem xs={12} md={3}>
                  <label className={classesLocal.inputDescription}>
                    {t("initiatives.scope_margin_ratio")}
                  </label>
                  {initiativesBusy ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        height: "35px",
                        marginTop: "-6px",
                        borderBottom: "1px solid " + grayColor[4],
                        paddingBottom: "8px",
                      }}
                    >
                      <InputAdornment
                        position="start"
                        style={{ marginRight: "8px" }}
                      >
                        ×
                      </InputAdornment>
                      <ThreeDots
                        color={grayColor[0]}
                        ariaLabel="loading"
                        width={"20px"}
                        height={"12px"}
                      />
                      <InputAdornment
                        position="end"
                        style={{ marginLeft: "8px" }}
                      >
                        %
                      </InputAdornment>
                    </div>
                  ) : (
                    <CustomInput
                      id="scope_margin_ratio"
                      value={initiativeForm.scopeMarginRatio}
                      formControlProps={{
                        fullWidth: true,
                        style: { paddingTop: "0", marginTop: "-6px" },
                      }}
                      inputProps={{
                        type: "number",
                        disabled: true,
                        startAdornment: (
                          <InputAdornment position="start">×</InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">%</InputAdornment>
                        ),
                      }}
                      client
                    />
                  )}
                </GridItem>

                {/* Global Benefit Input with '=' and Refresh integrated */}
                <GridItem xs={12} md={3}>
                  <label className={classesLocal.inputDescription}>
                    {t("initiatives.benefit_global")}
                  </label>
                  <CustomInput
                    id="benefit_global"
                    value={initiativeForm.benefitGlobal}
                    onBlur={(e) => handleInputBlur(e)}
                    formControlProps={{
                      fullWidth: true,
                      style: { paddingTop: "0", marginTop: "-6px" },
                    }}
                    inputProps={{
                      type: "number",
                      disabled: true,
                      startAdornment: (
                        <InputAdornment position="start">=</InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            %
                            <Tooltip
                              placement={"top"}
                              title={t("initiatives.refresh")}
                            >
                              <Refresh
                                className={classesLocal.refreshIcon}
                                style={{ marginLeft: "8px" }}
                                disabled={
                                  screenBusy ||
                                  initiativesBusy ||
                                  !inputsStatusOk()
                                }
                                onClick={() => {
                                  updateInitData();
                                }}
                              />
                            </Tooltip>
                          </div>
                        </InputAdornment>
                      ),
                    }}
                    client
                  />
                </GridItem>
              </GridContainer>
            </CardBody>
          </Card>
        </div>
      </div>

      {/* Fifth Card - Dates Title and Content Side by Side */}
      <div style={{ display: "flex", marginBottom: "10px" }}>
        {/* Title Card - 15% width */}
        <div style={{ width: "15%", marginRight: "10px" }}>
          <Card
            className={cx({
              [classesLocal.card]: true,
            })}
            style={{ height: "100%", margin: "0" }}
          >
            <div
              style={{
                color: grayColor[3],
                backgroundColor: grayColor[8] + "44",
                textAlign: "center",
                padding: "15px 10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "column",
                }}
              >
                {t("initiatives.dates")}
                {isDatesComplete() ? (
                  <Check style={{ color: successColor[0], fontSize: "20px" }} />
                ) : (
                  <Clear style={{ color: dangerColor[0], fontSize: "20px" }} />
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* Content Card - 85% width */}
        <div style={{ width: "85%" }}>
          <Card
            className={cx({
              [classesLocal.card]: true,
            })}
            style={{ height: "100%", margin: "0" }}
          >
            <CardBody style={{ padding: "10px 15px 0 5px" }}>
              <GridContainer style={{ margin: "0" }}>
                {/* Row 3: Project Start, Capture Start, Capture End */}
                <GridItem xs={12} md={4}>
                  <label className={classesLocal.inputDescription}>
                    {t("initiatives.project_start")}
                  </label>
                  <DatePickerBenefitDates
                    value={initiativeForm.projectStart}
                    onChange={(e) => handleInputChange(e, "project_start")}
                    status={initiativeForm.projectStartStatus}
                    initiativesBusy={initiativesBusy}
                    selectedInitiative={selectedInitiative}
                    initiatives={initiatives}
                    isValidDate={isValidDate}
                  />
                </GridItem>

                <GridItem xs={12} md={4}>
                  <label className={classesLocal.inputDescription}>
                    {t("initiatives.capture_start")}
                  </label>
                  <DatePickerBenefitDates
                    value={initiativeForm.benefitStart}
                    onChange={(e) => handleInputChange(e, "benefits_start")}
                    status={initiativeForm.benefitStartStatus}
                    initiativesBusy={initiativesBusy}
                    selectedInitiative={selectedInitiative}
                    initiatives={initiatives}
                    isValidDate={isValidDate}
                  />
                </GridItem>

                <GridItem xs={12} md={4}>
                  <label className={classesLocal.inputDescription}>
                    {t("initiatives.capture_full_potential")}
                  </label>
                  <DatePickerBenefitDates
                    value={initiativeForm.benefitEnd}
                    onChange={(e) => handleInputChange(e, "benefits_end")}
                    status={initiativeForm.benefitEndStatus}
                    initiativesBusy={initiativesBusy}
                    selectedInitiative={selectedInitiative}
                    initiatives={initiatives}
                    isValidDate={isValidDate}
                  />
                </GridItem>
              </GridContainer>
            </CardBody>
          </Card>
        </div>
      </div>

      {/* Sixth Card - Tags Title and Content Side by Side */}
      <div style={{ display: "flex", marginBottom: "10px" }}>
        {/* Title Card - 15% width */}
        <div style={{ width: "15%", marginRight: "10px" }}>
          <Card
            className={cx({
              [classesLocal.card]: true,
            })}
            style={{ height: "100%", margin: "0" }}
          >
            <div
              style={{
                color: grayColor[3],
                backgroundColor: grayColor[8] + "44",
                textAlign: "center",
                padding: "15px 10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              {t("initiatives.tags")}
            </div>
          </Card>
        </div>

        {/* Content Card - 85% width */}
        <div style={{ width: "85%" }}>
          <Card
            className={cx({
              [classesLocal.card]: true,
            })}
            style={{ height: "100%", margin: "0" }}
          >
            <CardBody style={{ padding: "8px 15px 0 5px" }}>
              <GridContainer style={{ margin: "0" }}>
                <GridItem xs={12}>
                  <label className={classesLocal.inputDescription}>
                    {t("initiatives.tags")}
                  </label>
                  <GridContainer
                    alignItems={"center"}
                    style={{
                      marginBottom: "5px",
                      minHeight: "30px",
                    }}
                  >
                    <GridItem>
                      <FilterDropdown
                        dropPlacement={"bottom-start"}
                        hoverColor={"client"}
                        dropdownHeader={t("initiatives.select_tags")}
                        dropdownList={tagNames}
                        dropdownActiveItems={activeTagIndexes}
                        dropdownFilterSequence={[]}
                        tooltip={t("initiatives.select_tags")}
                        onClick={(index) => {
                          // Direct tag selection - bypass filter dialog workflow
                          if (
                            typeof index !== "number" ||
                            index < 0 ||
                            index >= tags.length
                          )
                            return;
                          const tagId = tags[index].id;
                          setInitiativeFormValue(
                            "tags",
                            initiativeForm.tags.includes(tagId)
                              ? initiativeForm.tags.filter((id) => id !== tagId)
                              : [...initiativeForm.tags, tagId],
                          );
                        }}
                      />
                    </GridItem>
                    <GridItem xs={true}>{selectedTagsFilters()}</GridItem>
                  </GridContainer>
                </GridItem>
              </GridContainer>
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  );
};

InitiativeDetailsCard.propTypes = {
  // Card state and data
  selectedInitiative: PropTypes.any,
  initiativeForm: PropTypes.object.isRequired,
  setInitiativeFormValue: PropTypes.func.isRequired,

  // Status flags
  initiativesBusy: PropTypes.bool,
  screenBusy: PropTypes.bool,

  // Data arrays
  stages: PropTypes.array,
  pnlStructure: PropTypes.array,
  tags: PropTypes.array,
  initiatives: PropTypes.object,

  // Functions
  checkAndSaveInitiative: PropTypes.func,
  updateInitData: PropTypes.func,

  handlers: PropTypes.object,

  // Filter related
  metaLoaded: PropTypes.bool,
  filtersListTranslated: PropTypes.func,
  filtersActiveList: PropTypes.func,
  filtersSequence: PropTypes.func,
  filterMenuClickHandler: PropTypes.func,
  currentScopeFilters: PropTypes.func,
  hasNoScopeFilters: PropTypes.func,
  selectedTagsFilters: PropTypes.func,

  // Refs
  shortDescriptionInput: PropTypes.object,
  // Additional props for longDescription
  initLongDescriptionRef: PropTypes.object,
};

export default InitiativeDetailsCard;
